import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Car, UserCheck, UserX, LogIn, Loader2, CreditCard, Upload, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchCustomers, createCustomer, deleteCustomer, fetchResellerProfile, createBillingPortalSession, markDeviceStockDeployed, Customer } from "@/lib/reseller-api";
import { fetchUsers, fetchDevices, loginAsUser, fetchPermissionsMap, userDevicesFromMap, linkDevice, unlinkDevice } from "@/lib/traccar-api";
import { EditUserGroupsDialog } from "@/components/users/EditUserGroupsDialog";
import { EditUserDialog } from "@/components/users/EditUserDialog";
import { CustomerDialog } from "@/components/customers/CustomerDialog";
import * as XLSX from 'xlsx';

const statusColors: Record<string, string> = {
  active: "bg-green-500",
  inactive: "bg-gray-500",
  suspended: "bg-red-500",
};

export default function Customers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<'name' | 'email' | 'phone' | 'tax_id' | 'status' | 'vehicles' | 'city' | 'town' | 'postal_code' | 'state' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [isOpen, setIsOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Partial<Customer>[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [manageDevicesCustomer, setManageDevicesCustomer] = useState<Customer | null>(null);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [savingDevices, setSavingDevices] = useState(false);
  const [deviceChanges, setDeviceChanges] = useState<Record<number, boolean>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: profile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: fetchResellerProfile,
  });

  const billingPortalMutation = useMutation({
    mutationFn: (customerId: string) => createBillingPortalSession(customerId, window.location.href),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const { data: traccarUsers = [] } = useQuery({
    queryKey: ["traccar-users"],
    queryFn: fetchUsers,
  });

  const traccarUserIds = customers
    .filter((c) => c.traccar_user_id)
    .map((c) => c.traccar_user_id!);

  const { data: allDevices = [], isSuccess: devicesLoaded } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
  });

  const { data: permsMap, isSuccess: permissionsLoaded } = useQuery({
    queryKey: ["permissions-map"],
    queryFn: fetchPermissionsMap,
  });

  const { data: deviceCounts = {} } = useQuery({
    queryKey: ["customer-device-counts", traccarUserIds, permsMap, allDevices],
    queryFn: () => {
      const counts: Record<number, number> = {};
      for (const userId of traccarUserIds) {
        counts[userId] = userDevicesFromMap(userId, allDevices, permsMap!).length;
      }
      return counts;
    },
    enabled: traccarUserIds.length > 0 && allDevices.length > 0 && !!permsMap,
  });

  type ReportRow = { customer: string; deviceName: string; uniqueId: string; make: string; model: string };
  const { data: reportRows = [], isLoading: reportLoading } = useQuery<ReportRow[]>({
    queryKey: ["customer-vehicles-report", customers, permsMap, allDevices],
    queryFn: () => {
      const rows: ReportRow[] = [];
      for (const c of customers.filter(c => c.traccar_user_id)) {
        const devices = userDevicesFromMap(c.traccar_user_id!, allDevices, permsMap!);
        devices.forEach(d => rows.push({
          customer: c.name,
          deviceName: d.name,
          uniqueId: d.uniqueId,
          make: String(d.attributes?.make || ''),
          model: String(d.attributes?.vehicleModel || ''),
        }));
      }
      return rows.sort((a, b) => a.customer.localeCompare(b.customer) || a.deviceName.localeCompare(b.deviceName));
    },
    enabled: isReportOpen && traccarUserIds.length > 0 && allDevices.length > 0 && !!permsMap,
    staleTime: 60 * 1000,
  });

  const exportReportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportRows.map(r => ({
      'Customer': r.customer,
      'Vehicle': r.deviceName,
      'Device ID': r.uniqueId,
      'Make': r.make,
      'Model': r.model,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'customer-vehicles-report.xlsx');
  };

  const userDeviceIds = new Set(manageDevicesCustomer?.traccar_user_id && permsMap
    ? userDevicesFromMap(manageDevicesCustomer.traccar_user_id, allDevices, permsMap).map(d => d.id)
    : []);
  const groupIds = new Set(permsMap?.groupsByUser[manageDevicesCustomer?.traccar_user_id ?? 0] ?? []);
  const inheritedDeviceIds = new Set(allDevices.filter(d => d.groupId != null && groupIds.has(d.groupId)).map(d => d.id));
  const visibleDevices = allDevices.filter(d =>
    `${d.name} ${d.uniqueId}`.toLowerCase().includes(deviceSearch.trim().toLowerCase()));
  const isDeviceSelected = (id: number) => deviceChanges[id] ?? userDeviceIds.has(id);
  const pendingDeviceChanges = Object.entries(deviceChanges).filter(([id, selected]) => selected !== userDeviceIds.has(Number(id)));

  const handleSaveDevices = async () => {
    if (!manageDevicesCustomer?.traccar_user_id || savingDevices) return;
    const userId = manageDevicesCustomer.traccar_user_id;
    setSavingDevices(true);
    const failedChanges: Record<number, boolean> = {};
    let failures = 0;
    let stockFailures = 0;
    const savedChanges: Record<number, boolean> = {};
    for (const [id, selected] of pendingDeviceChanges) {
      const deviceId = Number(id);
      try {
        if (selected) await linkDevice(userId, deviceId);
        else await unlinkDevice(userId, deviceId);
      } catch {
        failedChanges[deviceId] = selected;
        failures++;
        continue;
      }
      savedChanges[deviceId] = selected;
      if (selected) {
        try {
          await markDeviceStockDeployed({ traccar_device_id: deviceId, imei: allDevices.find(d => d.id === deviceId)?.uniqueId });
        } catch {
          stockFailures++;
        }
      }
    }
    queryClient.setQueryData(["permissions-map"], (current: typeof permsMap) => {
      if (!current) return current;
      const ids = new Set(current.devicesByUser[userId] ?? []);
      for (const [id, selected] of Object.entries(savedChanges)) {
        if (selected) ids.add(Number(id));
        else ids.delete(Number(id));
      }
      return { ...current, devicesByUser: { ...current.devicesByUser, [userId]: [...ids] } };
    });
    await queryClient.invalidateQueries({ queryKey: ["permissions-map"] });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["user-devices"] }),
      queryClient.invalidateQueries({ queryKey: ["customer-device-counts"] }),
      queryClient.invalidateQueries({ queryKey: ["customer-vehicles-report"] }),
      queryClient.invalidateQueries({ queryKey: ["device-stock"] }),
    ]);
    setDeviceChanges(failedChanges);
    setSavingDevices(false);
    if (failures || stockFailures) {
      toast({ title: t("common.error"), description: t("reseller.customer.bulkSaveFailed", { count: failures, stock: stockFailures }), variant: "destructive" });
    } else {
      toast({ title: t("reseller.customer.vehiclesSaved") });
      setManageDevicesCustomer(null);
    }
  };

  const openDialog = (customer?: Customer) => {
    setEditingCustomer(customer ?? null);
    setIsOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["customers"]}).then();
      toast({ title: t("reseller.customer.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const loginAsMutation = useMutation({
    mutationFn: async (traccarUserId: number) => {
      const traccarUser = traccarUsers.find((user) => user.id === traccarUserId);
      if (traccarUser?.disabled) {
        throw new Error(t("users.disabledUserCannotLogin"));
      }
      const response = await loginAsUser(traccarUserId);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t("users.loginAsError"));
      }
      return traccarUserId;
    },
    onSuccess: () => {
      window.location.href = "/";
    },
    onError: (error: Error) => {
      toast({ title: t("users.loginAsError"), description: error.message, variant: "destructive" });
    },
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (!sortField) return 0;
    let aVal: string | number, bVal: string | number;
    if (sortField === 'vehicles') {
      aVal = a.traccar_user_id ? (deviceCounts[a.traccar_user_id] ?? 0) : 0;
      bVal = b.traccar_user_id ? (deviceCounts[b.traccar_user_id] ?? 0) : 0;
    } else {
      aVal = (a[sortField] ?? '').toString().toLowerCase();
      bVal = (b[sortField] ?? '').toString().toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const raw = (ev.target?.result as string).replace(/^\uFEFF/, ""); // strip BOM
      const lines = raw.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        toast({ title: t("common.error"), description: t("reseller.customer.importEmpty"), variant: "destructive" });
        return;
      }
      const delimiter = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
      const splitLine = (line: string) => line.split(delimiter).map((v) => v.trim().replace(/^"|"$/g, ""));
      const headers = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
      const rows = lines.slice(1).map((line) => {
        const values = splitLine(line);
        const row: Partial<Customer> = {};
        headers.forEach((header, i) => {
          (row as any)[header] = values[i] || null;
        });
        return row;
      }).filter((r) => r.name);
      if (rows.length === 0) {
        toast({ title: t("common.error"), description: t("reseller.customer.importEmpty"), variant: "destructive" });
        return;
      }
      setImportRows(rows);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    setIsImporting(true);
    let success = 0, failed = 0;
    for (const row of importRows) {
      try {
        const customerType = row.customer_type === "company" ? "company" : "person";
        await createCustomer({ ...row, customer_type: customerType, status: "active" });
        success++;
      } catch {
        failed++;
      }
    }
    setIsImporting(false);
    setIsImportOpen(false);
    setImportRows([]);
    queryClient.invalidateQueries({ queryKey: ["customers"] });
    toast({ title: t("reseller.customer.importDone", { success, failed }) });
  };

  const getTraccarUserLabel = (userId: number | null) => {
    if (!userId) return null;
    const user = traccarUsers.find((u) => u.id === userId);
    return user ? user.email : `#${userId}`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold"></h1>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 whitespace-nowrap">
                <span className="text-sm text-muted-foreground">
                  {filteredCustomers.length !== customers.length
                    ? t("reseller.customer.totalFiltered", { filtered: filteredCustomers.length, total: customers.length })
                    : t("reseller.customer.total", { count: customers.length })}
                </span>
                {Object.keys(deviceCounts).length > 0 && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Car className="h-3.5 w-3.5" />
                      {t("reseller.customer.totalDevices", { count: Object.values(deviceCounts).reduce((s, n) => s + n, 0) })}
                    </span>
                  </>
                )}
              </div>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("reseller.customer.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />

              </div>
              <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline"><FileSpreadsheet className="h-4 w-4 mr-2" />{t("reseller.customer.report")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>{t("reseller.customer.vehiclesReport")}</DialogTitle>
                  </DialogHeader>
                  <div className="flex justify-end mb-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={exportReportExcel} disabled={reportRows.length === 0}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />{t("devices.exportExcel")}
                    </Button>
                  </div>
                  {reportLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="overflow-y-auto flex-1 border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("reseller.customer.name")}</TableHead>
                            <TableHead>{t("reseller.customer.vehicleName")}</TableHead>
                            <TableHead>{t("reseller.customer.make")}</TableHead>
                            <TableHead>{t("reseller.customer.model")}</TableHead>
                            <TableHead>{t("reseller.customer.vehicleId")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                {t("reseller.customer.noVehiclesReport")}
                              </TableCell>
                            </TableRow>
                          ) : reportRows.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{row.customer}</TableCell>
                              <TableCell>{row.deviceName}</TableCell>
                              <TableCell>{row.make}</TableCell>
                              <TableCell>{row.model}</TableCell>
                              <TableCell className="font-mono text-sm">{row.uniqueId}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={isImportOpen} onOpenChange={(open) => { setIsImportOpen(open); if (!open) setImportRows([]); }}>
                <DialogTrigger asChild>
                  <Button variant="outline"><Upload className="h-4 w-4 mr-2" />{t("reseller.customer.import")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t("reseller.customer.import")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{t("reseller.customer.importHint")}</p>
                    <code className="block text-xs bg-muted p-2 rounded">name,email,phone,tax_id,customer_type,address,city,town,state,postal_code,notes</code>
                    <Input type="file" accept=".csv,.tsv,.txt" onChange={handleImportFile} />
                    {importRows.length > 0 && (() => {
                      const cols = Object.keys(importRows[0]);
                      return (
                      <>
                        <p className="text-sm font-medium">{t("reseller.customer.importPreview", { count: importRows.length })}</p>
                        <div className="overflow-x-auto border rounded">
                          <table className="text-xs w-full">
                            <thead className="bg-muted">
                              <tr>
                                {cols.map((col) => (
                                  <th key={col} className="px-2 py-1 text-left whitespace-nowrap">{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importRows.map((row, i) => (
                                <tr key={i} className="border-t">
                                  {cols.map((col) => (
                                    <td key={col} className="px-2 py-1 whitespace-nowrap">{(row as any)[col] || "-"}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Button onClick={handleImport} disabled={isImporting} className="w-full">
                          {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                          {t("reseller.customer.importConfirm", { count: importRows.length })}
                        </Button>
                      </>
                      );
                    })()}
                  </div>
                </DialogContent>
              </Dialog>
              <Button onClick={() => openDialog()}><Plus className="h-4 w-4 mr-2" /> {t("reseller.customer.add")}</Button>
              <CustomerDialog
                open={isOpen}
                onOpenChange={(open) => { setIsOpen(open); if (!open) setEditingCustomer(null); }}
                editingCustomer={editingCustomer}
                defaultTraccarMode={editingCustomer ? undefined : 'new'}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.customer.noCustomers")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('name')}><span className="flex items-center">{t("reseller.customer.name")}<SortIcon field="name" /></span></TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('email')}><span className="flex items-center">{t("reseller.customer.contact")}<SortIcon field="email" /></span></TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('tax_id')}><span className="flex items-center">{t("reseller.customer.taxId")}<SortIcon field="tax_id" /></span></TableHead>
                    <TableHead>{t("reseller.customer.traccarLogin")}</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('vehicles')}><span className="flex items-center">{t("reseller.customer.vehicleCount")}<SortIcon field="vehicles" /></span></TableHead>
                    <TableHead>{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[customer.status] + " text-[10px] px-1.5 py-0"}>{t(`reseller.customer.statuses.${customer.status}`)}</Badge>
                          <span className="font-medium">{customer.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs">
                          {customer.email && <span>{customer.email}</span>}
                          {customer.phone && <span className="text-muted-foreground">{customer.phone}</span>}
                          {[customer.town, customer.city, customer.state].filter(Boolean).length > 0 && (
                            <span className="text-muted-foreground">{[customer.town, customer.city, customer.state].filter(Boolean).join(", ")}</span>
                          )}
                          {!customer.email && !customer.phone && !customer.town && !customer.city && !customer.state && "-"}
                        </div>
                      </TableCell>
                      <TableCell>{customer.tax_id || "-"}</TableCell>
                      <TableCell>
                        {customer.traccar_user_id ? (() => {
                          const traccarUser = traccarUsers.find((u) => u.id === customer.traccar_user_id);
                          const label = getTraccarUserLabel(customer.traccar_user_id);
                          const content = (
                            <div
                              className={`flex items-center gap-1 text-xs ${traccarUser ? "cursor-pointer hover:underline" : ""}`}
                              title={label ?? undefined}
                            >
                              <UserCheck className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              <span className="truncate max-w-[6rem]">{label}</span>
                            </div>
                          );
                          return traccarUser ? (
                            <EditUserDialog user={traccarUser} trigger={content} />
                          ) : content;
                        })() : (
                          <UserX className="h-3.5 w-3.5 text-muted-foreground" title={t("reseller.customer.noTraccarUser")} />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Car className="h-4 w-4" />
                          {customer.traccar_user_id ? (deviceCounts[customer.traccar_user_id] ?? 0) : 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-0">
                          {profile?.stripe_account_id && customer.stripe_customer_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title={t("reseller.stripe.billingPortal")}
                              onClick={() => billingPortalMutation.mutate(customer.id)}
                              disabled={billingPortalMutation.isPending}
                            >
                              {billingPortalMutation.isPending && billingPortalMutation.variables === customer.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CreditCard className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                          {customer.traccar_user_id && (
                            <>
                              {(() => {
                                const traccarUser = traccarUsers.find(u => u.id === customer.traccar_user_id);
                                return (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title={traccarUser?.disabled ? t("users.disabledUserCannotLogin") : t("users.loginAs")}
                                    onClick={() => loginAsMutation.mutate(customer.traccar_user_id!)}
                                    disabled={loginAsMutation.isPending || traccarUser?.disabled}
                                  >
                                    {loginAsMutation.isPending && loginAsMutation.variables === customer.traccar_user_id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <LogIn className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                );
                              })()}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title={t("reseller.customer.manageDevices")}
                                onClick={() => { setDeviceSearch(""); setDeviceChanges({}); setManageDevicesCustomer(customer); }}
                                disabled={!permissionsLoaded || !devicesLoaded}
                              >
                                <Car className="h-3.5 w-3.5" />
                              </Button>
                              {(() => {
                                const traccarUser = traccarUsers.find(u => u.id === customer.traccar_user_id);
                                return traccarUser ? (
                                  <EditUserGroupsDialog
                                    user={traccarUser}
                                    singleGroup
                                    trigger={
                                      <Button variant="ghost" size="icon" className="h-7 w-7" title={t("users.editGroups")}>
                                        <Layers className="h-3.5 w-3.5" />
                                      </Button>
                                    }
                                  />
                                ) : null;
                              })()}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openDialog(customer)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              if (confirm(t("reseller.common.deleteConfirm"))) {
                                deleteMutation.mutate(customer.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Manage devices dialog */}
      <Dialog open={!!manageDevicesCustomer} onOpenChange={(open) => { if (!open && !savingDevices) setManageDevicesCustomer(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{manageDevicesCustomer?.name} — {t("reseller.customer.manageDevices")}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder={t("devices.search")}
            value={deviceSearch}
            onChange={e => setDeviceSearch(e.target.value)}
            className="mt-2"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" disabled={savingDevices} onClick={() => setDeviceChanges(previous => ({ ...previous, ...Object.fromEntries(visibleDevices.filter(d => !inheritedDeviceIds.has(d.id)).map(d => [d.id, true])) }))}>{t("reseller.customer.selectVisible")}</Button>
            <Button variant="outline" size="sm" disabled={savingDevices} onClick={() => setDeviceChanges(previous => ({ ...previous, ...Object.fromEntries(visibleDevices.filter(d => !inheritedDeviceIds.has(d.id)).map(d => [d.id, false])) }))}>{t("reseller.customer.clearVisible")}</Button>
            <span className="text-sm text-muted-foreground">{t("reseller.customer.vehiclesSelected", { count: allDevices.filter(d => isDeviceSelected(d.id)).length })}</span>
          </div>
          <div className="overflow-y-auto flex-1 border rounded mt-2">
            {visibleDevices.length === 0 && <p className="p-4 text-sm text-muted-foreground">{t("common.noResults")}</p>}
            {visibleDevices.map(device => {
                const linked = isDeviceSelected(device.id);
                return (
                  <label
                    key={device.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={linked}
                      disabled={savingDevices || inheritedDeviceIds.has(device.id)}
                      onChange={() => setDeviceChanges(previous => ({ ...previous, [device.id]: !linked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{device.name}{inheritedDeviceIds.has(device.id) && <span className="block text-xs text-muted-foreground">{t("reseller.customer.vehicleFromGroup")}</span>}</span>
                    {device.uniqueId && !device.uniqueId.startsWith('not_installed_') && <span className="text-xs text-muted-foreground ml-auto">{device.uniqueId}</span>}
                  </label>
                );
              })}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={savingDevices} onClick={() => setManageDevicesCustomer(null)}>{t("common.cancel")}</Button>
            <Button disabled={savingDevices || pendingDeviceChanges.length === 0} onClick={handleSaveDevices}>
              {savingDevices && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}
